// ShopApprovalCard.jsx
// REFACTORED — swipe right=approve, left=reject, down=partial
// Merchant-side card for approving/rejecting customer purchase claims

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";
import SwipeCard from "./SwipeCard";

const ShopApprovalCard = ({ claim, onRefresh }) => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;

    const [loading,        setLoading]        = useState(false);
    const [message,        setMessage]        = useState(null);
    const [done,           setDone]           = useState(false);
    const [showPartial,    setShowPartial]    = useState(false);
    const [partialPoints,  setPartialPoints]  = useState(
        Math.floor((claim?.calculatedStreakPoints || 0) / 2)
    );

    if (done || claim.status !== "Pending") return null;

    // ── Approve Full ────────────────────────────────────────────────
    const handleApprove = async () => {
        try {
            setLoading(true);
            const res = await fetchData({
                url: "/pd/streaks/claim-approve",
                method: "post",
                data: {
                    claimId:             claim._id,
                    approvedBy:          authState?.user?._id,
                    approvedStreakPoints: claim.calculatedStreakPoints,
                    approvalRemarks:     "Approved by merchant",
                },
                headers: { Authorization: token },
            });
            if (res?.success) {
                setMessage({ type: "success", text: "✅ Claim approved" });
                setTimeout(() => { setDone(true); onRefresh?.(); }, 1200);
            } else {
                setMessage({ type: "warning", text: res?.message || "Approval failed" });
            }
        } catch (err) {
            setMessage({ type: "error", text: "Approval error" });
        } finally {
            setLoading(false);
        }
    };

    // ── Reject ──────────────────────────────────────────────────────
    const handleReject = async () => {
        try {
            setLoading(true);
            const res = await fetchData({
                url: "/pd/streaks/claim-reject",
                method: "post",
                data: {
                    claimId:         claim._id,
                    approvedBy:      authState?.user?._id,
                    approvalRemarks: "Rejected by merchant",
                },
                headers: { Authorization: token },
            });
            if (res?.success) {
                setMessage({ type: "success", text: "❌ Claim rejected" });
                setTimeout(() => { setDone(true); onRefresh?.(); }, 1200);
            } else {
                setMessage({ type: "warning", text: res?.message || "Rejection failed" });
            }
        } catch (err) {
            setMessage({ type: "error", text: "Rejection error" });
        } finally {
            setLoading(false);
        }
    };

    // ── Partial Approve ─────────────────────────────────────────────
    const handlePartialApprove = async () => {
        try {
            setLoading(true);
            const res = await fetchData({
                url: "/pd/streaks/claim-partial-approve",
                method: "post",
                data: {
                    claimId:             claim._id,
                    approvedBy:          authState?.user?._id,
                    approvedStreakPoints: Number(partialPoints),
                    approvalRemarks:     "Partially approved by merchant",
                },
                headers: { Authorization: token },
            });
            if (res?.success) {
                setMessage({ type: "success", text: `⚡ ${partialPoints} pts partially approved` });
                setShowPartial(false);
                setTimeout(() => { setDone(true); onRefresh?.(); }, 1200);
            } else {
                setMessage({ type: "warning", text: res?.message || "Partial approval failed" });
            }
        } catch (err) {
            setMessage({ type: "error", text: "Partial approval error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-2 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <SwipeCard
                disabled={loading}
                onSwipeRight={handleApprove}
                onSwipeLeft={handleReject}
                onSwipeDown={() => setShowPartial(true)}
                rightLabel="✅ Approve"
                leftLabel="❌ Reject"
                downLabel="⚡ Partial"
            >
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 space-y-3">

                    {/* Header */}
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="text-cyan-300 font-bold text-sm">
                                {claim.customerName}
                            </div>
                            <div className="text-[11px] text-gray-200">
                                {claim.customerCode}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-green-400 font-black text-lg">
                                ₹{claim.billAmount}
                            </div>
                            <div className="text-[10px] text-gray-200">
                                Bill #{claim.billNo}
                            </div>
                        </div>
                    </div>

                    {/* Details */}
                    <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-100">
                        <div>📅 {new Date(claim.billDate).toLocaleDateString("en-IN")}</div>
                        <div>🏪 {claim.shopName || claim.shopkeeperName}</div>
                        <div className="col-span-2 flex items-center gap-2">
                            <span className="text-yellow-300 font-bold text-sm">
                                🎯 {claim.calculatedStreakPoints} pts
                            </span>
                            <span className="text-gray-200">calculated reward</span>
                        </div>
                    </div>

                    {/* Status pill */}
                    <div className="flex justify-between items-center">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-400 border border-yellow-700/40">
                            ⏳ Awaiting Your Action
                        </span>
                        <span className="text-[10px] text-gray-300">
                            {new Date(claim.createdAt).toLocaleDateString("en-IN")}
                        </span>
                    </div>

                    {/* Feedback */}
                    <AnimatePresence>
                        {message && (
                            <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className={`p-2 rounded-lg text-xs font-bold text-center ${
                                    message.type === "success"
                                        ? "bg-green-900/40 text-green-300 border border-green-700/40"
                                        : message.type === "warning"
                                        ? "bg-yellow-900/40 text-yellow-300 border border-yellow-700/40"
                                        : "bg-red-900/40 text-red-300 border border-red-700/40"
                                }`}
                            >
                                {message.text}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Fallback buttons for non-touch */}
                    {!message && (
                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={handleApprove}
                                disabled={loading}
                                className="w-fit px-2 py-1 rounded-md bg-green-500/20 border border-green-500/40 text-green-300 text-xs font-bold hover:bg-green-500/30 transition-colors"
                            >
                                ✅ Approve
                            </button>
                            <button
                                onClick={() => setShowPartial(true)}
                                disabled={loading}
                                className=" bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 text-xs font-bold w-fit px-2 py-1 rounded-md hover:bg-yellow-500/30 transition-colors"
                            >
                                ⚡ Partial
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={loading}
                                className="bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold w-fit px-2 py-1 rounded-md hover:bg-red-500/30 transition-colors"
                            >
                                ❌ Reject
                            </button>
                        </div>
                    )}
                </div>
            </SwipeCard>

            {/* Partial Approval Modal */}
            <AnimatePresence>
                {showPartial && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm p-3"
                        onClick={() => setShowPartial(false)}
                    >
                        <motion.div
                            initial={{ y: 200 }}
                            animate={{ y: 0 }}
                            exit={{ y: 200 }}
                            transition={{ type: "spring", damping: 25 }}
                            className="w-full max-w-md max-h-[85vh] overflow-y-auto bg-gray-900 border border-yellow-700/40 rounded-3xl p-4 space-y-4 mb-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="w-10 h-1 bg-gray-700 rounded-full mx-auto" />

                            <div className="text-yellow-300 font-bold text-base">
                                ⚡ Partial Approval
                            </div>

                            <div className="text-sm text-gray-100">
                                Max allowed:{" "}
                                <span className="text-white font-bold">
                                    {claim.calculatedStreakPoints} pts
                                </span>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs text-gray-200">
                                    Points to approve
                                </label>
                                <input
                                    type="number"
                                    value={partialPoints}
                                    min={1}
                                    max={claim.calculatedStreakPoints}
                                    onChange={(e) => setPartialPoints(Number(e.target.value))}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-xl px-4 py-3 text-white text-lg font-bold text-center"
                                />
                                {/* Quick fractions */}
                                <div className="flex gap-2">
                                    {[25, 50, 75].map((pct) => (
                                        <button
                                            key={pct}
                                            onClick={() =>
                                                setPartialPoints(
                                                    Math.floor(claim.calculatedStreakPoints * pct / 100)
                                                )
                                            }
                                            className="text-xs w-fit px-2 py-1 rounded-md bg-gray-800 border border-gray-700 text-gray-100 hover:border-yellow-500/50 transition-colors"
                                        >
                                            {pct}%
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowPartial(false)}
                                    className="w-fit px-2 py-1 rounded-md bg-gray-800 text-gray-100 text-sm font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePartialApprove}
                                    disabled={loading || partialPoints <= 0}
                                    className="w-fit px-2 py-1 rounded-md bg-yellow-500 text-black text-sm font-bold disabled:opacity-50"
                                >
                                    {loading ? "Processing..." : `Approve ${partialPoints} pts`}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ShopApprovalCard;
